/**
 * Single source of truth for Toxy's business vocabulary: labels, short
 * descriptions and colours for diagnoses, exit lanes and priorities.
 * Tailwind classes are written out literally so the JIT picks them up.
 */

export const EXIT_LANES = {
  'Put back online': { label: 'Put back online', description: "A good product customers can't find: put it on the website first.", color: '#2563eb' },
  'Write off / donate': { label: 'Write off / donate', description: 'Worth so little that handling it costs more than it brings in.', color: '#9ca3af' },
  Bulky: { label: 'Bulky', description: 'Big and not very valuable: storing it costs more than it is worth.', color: '#8b5a2b' },
  Collectible: { label: 'Collectible', description: 'Limited or special edition: sell to collectors, not lamp buyers.', color: '#db2777' },
  Signature: { label: 'Signature', description: 'Expensive design pieces with a few left: sell them one by one to the right person.', color: '#7c3aed' },
  'Kit / bundle': { label: 'Kit / bundle', description: 'Cheap items: sell in packs, kits or as add-ons, not one by one.', color: '#6b7a1f' },
  'B2B project lot': { label: 'B2B project lot', description: 'Enough identical pieces to furnish a hotel, office or restaurant.', color: '#0d9488' },
  'Outlet / marketplace': { label: 'Outlet / marketplace', description: 'Everything else: brand drop, outlet or a private-sale platform, depending on why it is stuck.', color: '#d97706' },
};

export const DIAGNOSES = {
  'Not on our website': { label: 'Not on our website', description: "We have it in stock, but it isn't on our website, so customers can't find it." },
  'Only sold to businesses': { label: 'Only sold to businesses', description: 'Business customers (projects, B2B) bought it this year, but nobody bought it on our website.' },
  'Sells, but we have too many': { label: 'Sells, but we have too many', description: 'It still sells, but so slowly that our stock will last for years.' },
  'Other colours/sizes sell': { label: 'Other colours/sizes sell', description: 'Other colours or sizes of this product sell well. This one does not.' },
  'Hardly anyone sees it': { label: 'Hardly anyone sees it', description: 'Our ads barely show it, or its product page is incomplete, so few people saw it.' },
  'No colour or size sells': { label: 'No colour or size sells', description: 'We have several colours/sizes of this product in stock and none of them sells.' },
  'Many views, no sales': { label: 'Many views, no sales', description: 'Lots of people saw it in our ads, but nobody bought it. A lower price probably will not help.' },
};

export const PRIORITIES = {
  'Act now': {
    label: 'Act now',
    short: 'Act now',
    description: 'A lot of money stuck: start here.',
    badge: 'bg-red-600 text-white ring-red-600 dark:bg-red-500/90 dark:ring-red-500/90',
  },
  Watch: {
    label: 'Watch',
    short: 'Watch',
    description: 'Not a problem yet: small fixes now prevent a bigger problem.',
    badge: 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/15 dark:text-sky-300 dark:ring-sky-500/30',
  },
  'This quarter': {
    label: 'This quarter',
    short: 'This quarter',
    description: 'Worth handling, after the Act now items.',
    badge: 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30',
  },
  'Batch it': {
    label: 'Batch it',
    short: 'Batch it',
    description: 'Small amounts: handle together (lots, kits, write-off), not one by one.',
    badge: 'bg-slate-50 text-slate-600 ring-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:ring-slate-500/30',
  },
};

export const UNIVERSES = {
  Toxic: { label: 'Toxic', description: 'Old, unsold stock with tied-up value.' },
  Watchlist: { label: 'Watchlist', description: 'Slowing down — at risk of becoming toxic.' },
  'Not on our website': { label: 'Not on our website', description: 'In stock but not listed online.' },
};

export const SHORTLIST_PURPOSES = {
  b2b_project: { label: 'B2B project' },
  idea: { label: 'Idea' },
  bundle: { label: 'Bundle' },
  other: { label: 'Other' },
};

export const FOCUS_OPTIONS = [
  { value: 'balanced', label: 'Balanced' },
  { value: 'looks', label: 'Looks' },
  { value: 'brand', label: 'Brand' },
  { value: 'price', label: 'Price' },
];

const FALLBACK_CHIP = 'bg-muted text-muted-foreground ring-border';

export const laneMeta = (lane) => EXIT_LANES[lane] || { label: lane, description: '', color: '#94a3b8', chip: FALLBACK_CHIP };
export const diagnosisMeta = (d) => DIAGNOSES[d] || { label: d, description: '' };
export const priorityMeta = (p) => PRIORITIES[p] || { label: p, short: p, description: '', badge: FALLBACK_CHIP };
export const universeMeta = (u) => UNIVERSES[u] || { label: u, description: '' };
export const purposeLabel = (p) => SHORTLIST_PURPOSES[p]?.label || p || '—';

/** Human labels for item attribute / missing-field keys. */
export const FIELD_LABELS = {
  brand: 'Brand',
  category: 'Category',
  product_type: 'Product type',
  designer: 'Designer',
  made_in: 'Made in',
  color: 'Colour',
  color_detail: 'Colour detail',
  google_category: 'Google category',
  dimensions: 'Dimensions',
  style: 'Style',
  material: 'Material',
  size: 'Size',
  dim_cm: 'Dimension (cm)',
  ip_rating: 'IP rating',
  kelvin: 'Colour temp.',
  watt: 'Wattage',
  socket: 'Socket',
  dimmable: 'Dimmable',
  outdoor: 'Outdoor',
  styles: 'Styles',
  season: 'Season',
  unit_m3: 'Volume / unit (m³)',
  ean: 'EAN',
  image: 'Image',
  image_url: 'Image',
  description: 'Description',
};
export const fieldLabel = (f) => FIELD_LABELS[f] || String(f).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
